import React, { FC, SetStateAction } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../../api/config";
import { FormFieldT, SelectOptionT } from "../../models/models";
import { TOAST_DURATION } from "../../utils/helpers/constants";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { countrySO } from "../../utils/helpers/selectOptions";
import { useTranslate } from "../../utils/hooks/useTranslate";
import Form from "../Form/Form";
import { ValidatorsE } from "../Form/yupValidations";
import { ContactsPageTypeE } from "../../models/enums";

import styles from "./styles.module.scss";

type ContactUsFormDataT = {
  company: string;
  companyType: SelectOptionT;
  country: SelectOptionT;
  emailAddress: string;
  emailBody: string;
  name: string;
  phoneNumber: number;
};

type ContactUsFormPropTypes = {
  setIsSubmitting: React.Dispatch<SetStateAction<boolean>>;
  setIsSubmitted: React.Dispatch<SetStateAction<boolean>>;
  overrideHandleSubmit?: (data: ContactUsFormDataT) => void;
  formId?: string;
};

const companyTypeSO: SelectOptionT[] = [
  { value: 1, label: "Advertising Agency" },
  { value: 2, label: "Post Production Company" },
  { value: 3, label: "Broadcaster" },
  { value: 4, label: "Brand/Advertiser" },
  { value: 5, label: "Production Company" },
  { value: 6, label: "Media Buyer Agency" },
  { value: 6, label: "Other" },
];

/**
 * Public contact us form (no need for authorization)
 */

const ContactUsForm: FC<ContactUsFormPropTypes> = (props) => {
  const {
    overrideHandleSubmit,
    setIsSubmitting,
    setIsSubmitted,
    formId = "public-contact-form",
  } = props;
  const { t } = useTranslate();

  const fields: FormFieldT[] = [
    {
      label: "Name",
      validation: ValidatorsE.REQUIRED_STRING,
    },
    {
      label: "Email Address",
      validation: ValidatorsE.EMAIL,
    },
    {
      label: "Company",
      validation: ValidatorsE.REQUIRED_STRING,
    },
    {
      label: "How Can We Help?",
      name: "emailBody",
      type: "textarea",
      style: !isMobileOrTablet()
        ? { gridArea: "3 / 3 / span 4 / 2" }
        : { order: 1 },
    },
    {
      label: "Company Type",
      selectOptions: companyTypeSO,
      placeholder: "Choose a company type",
      validation: ValidatorsE.SELECT_REQUIRED,
    },
    {
      label: "Country",
      selectOptions: countrySO,
      placeholder: "Choose a country",
      validation: ValidatorsE.SELECT_REQUIRED,
    },
    {
      label: "Phone Number",
      validation: ValidatorsE.PHONE_NUMBER,
    },
  ];

  const handleSubmit = async (data: ContactUsFormDataT) => {
    if (overrideHandleSubmit) {
      overrideHandleSubmit(data);
      return;
    }

    setIsSubmitting(true);
    const body = {
      Company: data.company,
      CompanyIndustry: data.companyType.label,
      Country: data.country.value,
      Email: data.emailAddress,
      EmailBody: data.emailBody,
      FullName: data.name,
      PhoneNumber: data.phoneNumber,
      PageType: window.location.pathname.includes("contactus")
        ? ContactsPageTypeE.CONTACT
        : ContactsPageTypeE.SUPPORT,
    };
    try {
      const res = await fetch(
        BASE_URL + "api/BusinessPages/LandingPageFormSubmit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      if (res.status === 200) {
        toast.success(
          t`Thank you for your interest. We will be in touch with you shortly.`,
          {
            autoClose: TOAST_DURATION,
          }
        );
        setIsSubmitted(true);
      } else if (res.status === 400 || res.status === 500) {
        toast.error(t`Something went wrong. Please try again.`, {
          autoClose: TOAST_DURATION,
        });
      }
    } catch (err) {
      toast(t`Error submitting form`, {
        type: "error",
        autoClose: TOAST_DURATION,
      });
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return <Form fields={fields} onSubmit={handleSubmit} formID={formId} />;
};

export default ContactUsForm;
